import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  AccountCard,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CurrencyAmountUI,
  DateUI,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  PrimaryFullScreenSpinner,
  Txt,
  toast,
} from "@repo/ui";
import { formatLongUnitAmount, NotFoundError } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Suspense } from "react";
import { CustodyAccountCard } from "~/components/Custody/CustodyAccountCard";
import { DetailRow } from "~/components/DetailRow/DetailRow";
import { FundDistributionStatusBadge } from "~/components/FundDistribution/FundDistributionStatusBadge";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { BusinessEntityColumnCell } from "~/components/tables/BusinessEntityColumnCell";
import { graphql } from "~/gql/client";
import type { AdminFundDistributionAllocationRowFragment } from "~/gql/client/graphql";
import { opsHead, useOpsTitle } from "~/head";

export const Route = createFileRoute(
  "/__auth/fund-distributions_/$fundDistributionId",
)({
  head: () => opsHead("Fund Distribution"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

function AllocationsTable({
  allocations,
  title,
}: {
  allocations: readonly AdminFundDistributionAllocationRowFragment[];
  title: ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        {allocations.length === 0 ? (
          <Txt variant="body" tone="secondary">
            No allocations.
          </Txt>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-secondary">
                <th className="py-2 font-medium">Business Entity</th>
                <th className="py-2 font-medium">Asset</th>
                <th className="py-2 text-right font-medium">Weighted Units</th>
                <th className="py-2 text-right font-medium">Allocated Units</th>
              </tr>
            </thead>
            <tbody>
              {allocations.map((a) => (
                <tr key={a.id} className="border-b last:border-b-0">
                  <td className="py-2 pr-4">
                    <BusinessEntityColumnCell
                      businessEntity={a.asset.businessEntity}
                    />
                  </td>
                  <td className="py-2 pr-4">
                    <div className="w-fit">
                      <AccountCard
                        variant="DEFAULT"
                        account={a.asset}
                        subText="PROVIDER"
                        linkTo={`/assets/${a.asset.id}`}
                      />
                    </div>
                  </td>
                  <td className="py-2 text-right">
                    {formatLongUnitAmount({ amount: a.weightedUnits })}
                  </td>
                  <td className="py-2 text-right">
                    {formatLongUnitAmount({ amount: a.allocatedUnits })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

function ConfirmButton({
  trigger,
  title,
  description,
  confirmLabel,
  onConfirm,
  loading,
}: {
  trigger: ReactNode;
  title: string;
  description: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  loading: boolean;
}) {
  return (
    <Dialog>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function View() {
  const { fundDistributionId } = Route.useParams();

  const { data, refetch } = useSuspenseQuery(
    graphql(`
      query AdminFundDistributionPage($id: ID!) {
        fundDistribution: node(id: $id) {
          id
          __typename
          ... on FundDistribution {
            shortRef
            status
            payDate
            periodStart
            periodEnd
            totalUnitAmount
            unitPrice
            unitPriceCurrency
            createdAt
            custodyAccount {
              ...CustodyAccountCard
              product {
                id
                internalName
              }
            }
            allocations {
              ...AdminFundDistributionAllocationRow
            }
          }
        }
      }

      fragment AdminFundDistributionAllocationRow on FundDistributionAllocation {
        id
        weightedUnits
        allocatedUnits
        asset {
          id
          ...AssetCardFields
          businessEntity {
            id
            name
            organisation {
              id
              name
            }
          }
        }
      }
    `),
    { variables: { id: fundDistributionId } },
  );

  const fundDistribution = data?.fundDistribution;
  if (!fundDistribution || fundDistribution.__typename !== "FundDistribution") {
    throw new NotFoundError();
  }

  useOpsTitle(`Fund Distribution #${fundDistribution.shortRef}`);

  const [voidDistribution, { loading: voidLoading }] = useMutation(
    graphql(`
      mutation AdminVoidFundDistribution($input: VoidFundDistributionInput!) {
        voidFundDistribution(input: $input) {
          fundDistribution {
            id
            status
          }
        }
      }
    `),
  );

  const onError = (action: string) => (error: { message?: string }) => {
    toast({
      kind: "error",
      autoHide: false,
      title: `Failed to ${action} fund distribution`,
      description: error.message ?? "Please try again later.",
    });
  };

  const handleVoid = async () => {
    await voidDistribution({
      variables: { input: { fundDistributionId } },
      onCompleted: () => {
        toast({ kind: "success", autoHide: true, title: "Voided" });
        void refetch();
      },
      onError: onError("void"),
    });
  };

  const canVoid = fundDistribution.status !== "VOID";

  return (
    <AdminPageContainer testId="admin-fund-distribution">
      <AdminPageHeader
        title={`Fund Distribution #${fundDistribution.shortRef}`}
        subtitle={fundDistribution.custodyAccount.product?.internalName ?? ""}
        buttons={
          <div className="flex items-center gap-2">
            <FundDistributionStatusBadge status={fundDistribution.status} />
            {canVoid && (
              <ConfirmButton
                trigger={<Button variant="outline">Void</Button>}
                title="Void fund distribution?"
                description="This voids the distribution and reverses any ledger transactions posted when it was reconciled (income recognition, reinvestment and per-asset allocations)."
                confirmLabel="Void"
                onConfirm={() => void handleVoid()}
                loading={voidLoading}
              />
            )}
          </div>
        }
      />

      <div className="flex flex-col gap-6">
        <Card>
          <CardContent className="pt-6">
            <DetailRow
              label="Status"
              value={
                <FundDistributionStatusBadge status={fundDistribution.status} />
              }
            />
            <DetailRow
              label="Custody Account"
              value={
                <div className="w-fit">
                  <CustodyAccountCard
                    custodyAccount={fundDistribution.custodyAccount}
                    link
                  />
                </div>
              }
            />
            <DetailRow
              label="Pay Date"
              value={<DateUI date={fundDistribution.payDate} />}
            />
            <DetailRow
              label="Period"
              value={
                <span className="flex items-center gap-1">
                  <DateUI date={fundDistribution.periodStart} />
                  <span className="text-secondary">→</span>
                  <DateUI date={fundDistribution.periodEnd} />
                </span>
              }
            />
            <DetailRow
              label="Total Units"
              value={formatLongUnitAmount({
                amount: fundDistribution.totalUnitAmount,
              })}
            />
            <DetailRow
              label="Unit Price"
              value={
                <CurrencyAmountUI
                  amount={fundDistribution.unitPrice}
                  currency={fundDistribution.unitPriceCurrency}
                />
              }
            />
            <DetailRow
              label="Created"
              value={<DateUI date={fundDistribution.createdAt} />}
            />
          </CardContent>
        </Card>

        <AllocationsTable
          title="Allocations"
          allocations={fundDistribution.allocations}
        />
      </div>
    </AdminPageContainer>
  );
}
